<template>
    <div
        class="h-100"
        ref="hotspotsContainer" 
        v-on:keydown.tab="tabFocusChoice($event)"
        v-on:keydown.enter="selectTabbedChoice($event)"
    >
        <div
            v-if="showLoadingSpinner"
            class="position-absolute w-100 d-flex justify-content-center align-items-center loading-spinner"
            :class="{ 'editor-container': editMode, 'full-screen': panel.display_full_screen }"
            :style="[viewportHeight]"
        >
            <div class="custom-spinner" role="status">
                <span class="sr-only">Loading</span>
            </div>
        </div>
        <div
            v-if="includeDragPrompt"
            class="position-absolute d-flex justify-content-center w-100 drag-msg-container"
            :class="{
                'drag-msg-padding-lg': panel.display_full_screen,
                'drag-msg-padding-sm': !panel.display_full_screen,
            }"
        >
            <span class="rounded text-light drag-msg" :class="{'drag-msg-show': showDragPrompt}">
                <span class="icon icon-lg icon-glyphicons-basic-view-full align-middle mr-1"></span>
                <span class="medium align-middle">{{ labelsTranslated.lookAround }}</span>
            </span>
        </div>
        <div
            :class="{ 'editor-container': editMode }"
            class="hotspots-viewport"
            :style="[viewportHeight]"
        >
            <canvas id="renderCanvas" :aria-description="accessibilityDescription" class="w-100 h-100 grab render-canvas"></canvas>
        </div>
        <video v-if="panel.panel_type === '3d_video'" :id="'video' + _uid" class="vr-video">
            <source :src="mediaSrc" type="video/mp4" />
        </video>
    </div>
</template>

<script>
import { hotspotColors } from "@/assets/js/settings"

// Only uncomment this during development - otherwise causes huge bundle sizes.
// import * as BABYLON from 'babylonjs';

import { Engine } from "@babylonjs/core/Engines/engine"
import { Scene } from "@babylonjs/core/scene"
import { PointerEventTypes } from "@babylonjs/core/Events"
import { Vector3 } from "@babylonjs/core/Maths/math"
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera"
import { PhotoDome } from "@babylonjs/core/Helpers/photoDome"
import { VideoDome } from "@babylonjs/core/Helpers/videoDome"
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial"
import { PointerDragBehavior } from "@babylonjs/core/Behaviors/Meshes"
import { HighlightLayer } from "@babylonjs/core/Layers"
import { Mesh, MeshBuilder } from "@babylonjs/core/Meshes"
import { ActionManager, ExecuteCodeAction } from "@babylonjs/core/Actions"
import { Color3 } from "@babylonjs/core/Maths/math.color"
import { Animation } from "@babylonjs/core/Animations/animation"
// Need these imports for everything to work (side-effect).
import { animatable } from "@babylonjs/core/Animations"
import { externalTexture } from "@babylonjs/core/Engines/Extensions"

const degreesToRadians = (2 * Math.PI) / 360
const materialAlphaBase = 0.88
const domeSphereRadius = 400
const defaultHotspotPositionRadius = 390

export default {
    name: "HotSpots360",
    props: {
        panel: {
            type: Object,
            required: true,
        },
        mediaSrc: {
            type: String,
            required: true,
        },
        choices: {
            type: Array,
            required: true,
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        enableTabbing: {
            type: Boolean,
            required: false,
            default: false,
        },
        showPrompts: {
            type: Boolean,
            required: false,
            default: true,
        },
        showLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        zoomLevel: {
            type: Number,
            required: false,
            default() {
                return 360
            },
        }
    },
    data() {
        return {
            engine: null,
            scene: null,
            camera: null,
            highlight: null,
            highlightActive: null,
            discs: [],
            loading: true,
            selectedChoice: null,
            tabbedChoiceIndex: 0,
            includeDragPrompt: this.showPrompts,
            showDragPrompt: null,
        }
    },
    computed: {
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        panelID() {
            return this.panel.id
        },
        panelHeight() {
            return this.panel.height || this.panel.elements[0].height || 700
        },
        viewportHeight() {
            if (this.editMode) {
                return {}
            } else if (this.panel.display_full_screen) {
                return { height: "100%" }
            } else {
                return { height: this.panelHeight + "px" }
            }
        },
        initialCameraAnglesSet() {
            return this.panel.init_camera_angle_alpha !== null && this.panel.init_camera_angle_beta !== null
        },
        accessibilityDescription() {
            if (this.panel.accessibility_description) {
                return this.panel.accessibility_description
            } else if (this.panel.elements && this.panel.elements[0].accessibility_alt_text) {
                return this.panel.elements[0].accessibility_alt_text
            }
            return "Interactive 360 degree environment"
        },
        showLoadingSpinner() {
            return this.loading || this.showLoading
        },
    },
    methods: {
        resetHotspots() {
            this.discs.forEach((m) => m.dispose())
            this.addHotspotsToScene()
        },
        // Initialize 360 video from an uploaded file.
        async init360Space() {
            // Don't initialize if the media source is missing.
            if (!this.mediaSrc) {
                this.loading = false
                return
            }

            let vm = this

            let canvas = document.getElementById("renderCanvas")
            let engine = new Engine(canvas, true, { stencil: true })

            let createScene = async function () {
                // Create the scene.
                let scene = new Scene(engine)
                scene.clearColor = new Color3(1, 1, 1)

                // This is a hack. This code adds a callback for all 'pick' events in the scene
                // (i.e. taps or clicks). When a disc is clicked, all its 'NothingTrigger' actions are triggered manually.
                // This fixes an issue where the default click callback on discs wasn't being triggered if the area of
                // the disc that was clicked was 'occluded' (i.e. hidden) by the 360 PhotoDome.
                scene.onPointerObservable.add((e) => {
                    if (e.type === PointerEventTypes.POINTERPICK) {
                        const picks = scene.multiPick(scene.pointerX, scene.pointerY)
                        picks.forEach(function(pick) {
                            let actionManager = pick.pickedMesh.actionManager
                            if (actionManager && actionManager.actions[0]) {
                                actionManager.actions.forEach(function(action) {
                                    // Only manually trigger actions with the 'NothingTrigger' trigger.
                                    if ([0, 1].includes(action.triggerOptions.trigger)) {
                                        action.execute()
                                    }
                                })
                            }
                        })
                    }
                })

                // This variable controls the "zoom level" of the camera. The higher the value, the
                // more zoomed out the viewport is.
                let cameraRadius = vm.zoomLevel
                // Create the camera.
                let camera = new ArcRotateCamera("Camera", -Math.PI / 2, Math.PI / 2, cameraRadius, Vector3.Zero(), scene)
                camera.attachControl(canvas, true)

                camera.inputs.attached.pointers.angularSensibilityX = -3000
                camera.inputs.attached.pointers.angularSensibilityY = -3000

                camera.lowerRadiusLimit = 5
                camera.upperRadiusLimit = cameraRadius

                // Remove the scrolling zoom effect.
                camera.inputs.attached.mousewheel.detachControl(canvas)

                vm.camera = camera

                let dome = null
                if (vm.panel.panel_type === 'image_hotspots') {
                    // Create the 360 degree photo dome.
                    dome = new PhotoDome(
                        "testdome",
                        vm.mediaSrc,
                        {
                            resolution: 64,
                            size: domeSphereRadius * 2,
                            useDirectMapping: true,
                        },
                        scene
                    )
                } else if (vm.panel.panel_type === '3d_video') {
                    // Create the 360 degree video dome.
                    let videoEl = document.getElementById("video" + vm._uid)
                    dome = new VideoDome(
                        "videodome",
                        videoEl,
                        {
                            resolution: 64,
                            clickToPlay: true,
                            useDirectMapping: false,
                            autoplay: true,
                            loop: false,
                            crossEyeMode: true,
                        },
                        scene
                    )
                    // Fire an event when the video finishes to let the parent know the video has ended.
                    videoEl.addEventListener(
                        "ended",
                        function () {
                            vm.$emit("video-ended")
                            // Resize the video upon completion in case switching out of full screen.
                            vm.$nextTick(() => {
                                vm.engine.resize()
                            })
                        },
                        false
                    )
                }

                dome.onLoadObservable.addOnce((t) => {
                    // Remove the loading spinner once the photo dome has loaded.
                    vm.loading = false
                    vm.$emit("update:loaded", true)
                    // Add the hotspots to the scene.
                    vm.addHotspotsToScene()
                    // After a delay, show a prompt to the user to 'look around'.
                    window.setTimeout(() => {
                        if (vm.showDragPrompt === null) {
                            vm.showDragPrompt = true
                        }
                    }, 1200)
                })

                return scene
            }

            // Create the scene.
            let scene = await createScene()

            this.scene = scene
            this.engine = engine
            this.highlight = new HighlightLayer("hl1", scene)
            this.highlightActive = new HighlightLayer("hl-selected", scene)

            // Run the render loop.
            engine.runRenderLoop(function () {
                scene.render()
            })

            // Handle window resize.
            window.addEventListener("resize", function () {
                engine.resize()
            })

            if (this.initialCameraAnglesSet) {
                // Set the initial camera angles.
                this.camera.alpha = this.panel.init_camera_angle_alpha
                this.camera.beta = this.panel.init_camera_angle_beta
            }

            // Create a callback to hide the message when the user interacts with the scene.
            let pointerDown = () => {
                vm.showDragPrompt = false
                // Remove the drag prompt from the scene entirely after the fade-out animation
                // is finished.
                window.setTimeout(() => vm.includeDragPrompt = false, 1000)
                // Only run the callback once - remove the listeners after the first run.
                canvas.removeEventListener("pointerdown", pointerDown)
                canvas.removeEventListener("touchmove", pointerDown)
            }
            canvas.addEventListener("pointerdown", pointerDown)
            canvas.addEventListener("touchmove", pointerDown)
        },
        // Add the configured hotspots to the scene.
        addHotspotsToScene() {
            let vm = this
            this.choices.forEach((choice) => vm.addHotspot(choice))
        },
        // Create and position a hotspot based on a choice.
        addHotspot(choice, focusCamera = false) {
            let vm = this

            // Create a double-sided disc.
            let disc = MeshBuilder.CreateDisc(
                "disc",
                {
                    radius: choice.marker_radius,
                    tessellation: 128,
                    sideOrientation: Mesh.DOUBLESIDE,
                    updatable: true,
                },
                this.scene
            )
            // This setting ensures that the discs are always visible, even
            // when they fall behind the surrounding photodome (i.e. occluded).
            disc.renderingGroupId = 1

            this.discs.push(disc)
            // Save the initial radius of the disc - we can't directly update this property
            // later on, but we can scale by a factor, which is calculated using this initial
            // scale value.
            choice.initialRadius = choice.marker_radius

            if (choice.position_x + choice.position_y + choice.position_z === 0) {
                // Initialize the position to right in front of the camera, if not set.
                choice.position_z = defaultHotspotPositionRadius // Horizontal axis.
                choice.position_x = 0 // Horizontal axis.
                choice.position_y = 0 // Vertical axis.
            }
            disc.position.z = choice.position_z
            disc.position.x = choice.position_x
            disc.position.y = choice.position_y

            let markerColor = this.getMarkerColorHex(choice)
            let colorRGB = hexToRgb(markerColor)
            let color = new Color3(colorRGB[0], colorRGB[1], colorRGB[2])

            // Set the material and color of the disc.
            let myMaterial = new StandardMaterial("myMaterial", this.scene)
            myMaterial.emissiveColor = color

            disc.material = myMaterial

            if (this.editMode) {
                myMaterial.alpha = materialAlphaBase
            } else {
                // Set the visibility of the hotspot using the material opacity.
                if (!(choice.marker_visible || vm.choiceIsRevealed(choice)) || choice.marker_color === "transparent") {
                    myMaterial.alpha = 0.0
                } else {
                    myMaterial.alpha = materialAlphaBase
                }
            }

            if (choice.rotation_x + choice.rotation_y + choice.rotation_z === 0) {
                // Initialize the rotation angles (euler angles) of the disc, if not set.
                choice.rotation_x = 0 // Rotation around x axis.
                choice.rotation_y = 0 // Rotation around y axis.
                choice.rotation_z = 0 // Rotation around z axis.
            }
            // Convert to radians and set the rotation angles.
            disc.rotation.x = choice.rotation_x * degreesToRadians
            disc.rotation.y = choice.rotation_y * degreesToRadians
            disc.rotation.z = choice.rotation_z * degreesToRadians

            if (this.editMode) {
                // Create the click-and-drag behaviour for the disc - by default, the drag plane
                // will be the camera direction.
                var pointerDragBehavior = new PointerDragBehavior()
                pointerDragBehavior.useObjectOrientationForDragging = false
                pointerDragBehavior.updateDragPlane = false

                pointerDragBehavior.onDragEndObservable.add((event) => {
                    let x = event.dragPlanePoint.x
                    let y = event.dragPlanePoint.y
                    let z = event.dragPlanePoint.z

                    // Make sure the hotspot is within the photodome.
                    vm.correctHotspotPosition(choice, x, y, z)

                    // Select the choice and display it in the parent menu.
                    vm.selectHotspot(choice)
                })

                disc.addBehavior(pointerDragBehavior)

                // Register the hotspot click to focus.
                disc.actionManager = new ActionManager(this.scene)
                disc.actionManager.registerAction(
                    new ExecuteCodeAction(
                        {
                            trigger: ActionManager.OnPickTrigger,
                        },
                        function () {
                            vm.selectHotspot(choice)
                            vm.$emit("open-menu")
                        }
                    )
                )
            } else {
                // Register the learner-facing hotspot actions.
                disc.actionManager = new ActionManager(this.scene)

                // Define and register the animation for changing the disc material disc opacity.
                const myAnim = new Animation(
                    "anim",
                    "alpha",
                    20,
                    Animation.ANIMATIONTYPE_FLOAT,
                    Animation.ANIMATIONLOOPMODE_CONSTANT
                )
                myAnim.setKeys([
                    { frame: 0, value: 0.0 },
                    { frame: 1, value: 0.9 },
                ])
                myMaterial.animations = [myAnim]

                // Register the action for selecting the choice.
                disc.actionManager.registerAction(
                    new ExecuteCodeAction(
                        {
                            // Using the 'Nothing Trigger' here because we're triggering disc clicks manually
                            // (see scene creation logic above).
                            trigger: ActionManager.NothingTrigger,
                        },
                        function () {
                            vm.selectedChoice = choice

                            myMaterial.alpha = materialAlphaBase
                            // Remove the default highlight effect on the disc, and add the loading-state disc.
                            let markerColorHex = vm.getMarkerColorHex(choice)
                            let rgbColor = hexToRgb(markerColorHex)
                            let color = new Color3(rgbColor[0], rgbColor[1], rgbColor[2])
                            vm.highlight.removeMesh(choice.disc)
                            vm.highlightActive.addMesh(choice.disc, color)
                            // Set the material and color of the disc.
                            myMaterial.emissiveColor = color

                            vm.$emit("select-choice", choice)
                        }
                    )
                )

                // Register pointer-over action.
                disc.actionManager.registerAction(
                    new ExecuteCodeAction(
                        {
                            trigger: ActionManager.OnPointerOverTrigger,
                        },
                        function () {
                            if (!(choice.marker_visible || vm.choiceIsRevealed(choice)) && choice.marker_color !== "transparent") {
                                vm.scene.beginAnimation(myMaterial, 0, 2)
                                vm.updateHotspotColor(choice)
                            } else if (choice.marker_color !== "transparent") {
                                myMaterial.alpha = 0.98
                            }
                        }
                    )
                )

                // Pointer out.
                disc.actionManager.registerAction(
                    new ExecuteCodeAction(
                        {
                            trigger: ActionManager.OnPointerOutTrigger,
                        },
                        function () {
                            if (!(choice.marker_visible || vm.choiceIsRevealed(choice) || choice.showLoading) || choice.marker_color == "transparent") {
                                vm.scene.beginAnimation(myMaterial, 2, 0)
                                vm.highlight.removeMesh(choice.disc)
                            } else {
                                myMaterial.alpha = materialAlphaBase
                            }
                        }
                    )
                )
            }

            if (this.editMode || ((choice.marker_visible || vm.choiceIsRevealed(choice)) && choice.marker_color !== 'transparent')) {
                // Highlight the disc.
                this.highlight.addMesh(disc, color)
            }

            // Add the pulse effect to the highlight.
            let alphaPulse = 0
            this.scene.registerBeforeRender(() => {
                alphaPulse += 0.08
                vm.highlight.blurHorizontalSize = 1.6 + Math.cos(alphaPulse) * 0.8
                vm.blurVerticalSize = 1.6 + Math.sin(alphaPulse) * 0.8
            })

            // Add the pulse effect to the highlight.
            let alphaPulseActive = 0
            this.scene.registerBeforeRender(() => {
                alphaPulseActive += 0.2
                vm.highlightActive.blurHorizontalSize = 2 + Math.cos(alphaPulseActive)
                vm.blurVerticalSize = 2 + Math.sin(alphaPulseActive)
            })

            // Store the disc instance on the choice.
            choice.disc = disc

            if (focusCamera) {
                this.focusHotspot(choice)
            }
        },
        // Update the position of a hotspot such that the center of the disc isn't
        // outside the radius of the photo dome.
        correctHotspotPosition(choice, x, y, z) {
            let idealDistance = defaultHotspotPositionRadius

            // Snap the disc to the closest point on the sphere represented by the
            // maximum allowed hotspot distance from the origin.
            let pointDistance = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2) + Math.pow(z, 2))
            if (
                pointDistance > idealDistance ||
                pointDistance < idealDistance - 6
            ) {
                choice.disc.position.x = (idealDistance * x) / pointDistance
                choice.disc.position.y = (idealDistance * y) / pointDistance
                choice.disc.position.z = (idealDistance * z) / pointDistance
            }

            // Update the final position values on the choice.
            choice.position_x = choice.disc.position.x
            choice.position_y = choice.disc.position.y
            choice.position_z = choice.disc.position.z
        },
        // Update the radius of a hotspot (visually, in Babylon) by modifying its scale.
        updateHotspotRadius(choice) {
            // Calculate the ratio to scale the disc by.
            let ratio = choice.marker_radius / choice.initialRadius
            choice.disc.scaling.x = ratio
            choice.disc.scaling.y = ratio
            choice.disc.scaling.z = ratio
            this.correctHotspotPosition(
                choice,
                choice.disc.position.x,
                choice.disc.position.y,
                choice.disc.position.z
            )
        },
        // Update the rotation angle of a hotspot (visually, in Babylon).
        updateHotspotAngle(choice) {
            choice.disc.rotation.x = choice.rotation_x * degreesToRadians
            choice.disc.rotation.y = choice.rotation_y * degreesToRadians
            choice.disc.rotation.z = choice.rotation_z * degreesToRadians
        },
        // Update a hotspot's color.
        updateHotspotColor(choice) {
            let markerColorHex = this.getMarkerColorHex(choice)
            let rgbColor = hexToRgb(markerColorHex)
            let color = new Color3(rgbColor[0], rgbColor[1], rgbColor[2])
            choice.disc.material.emissiveColor = color
            this.highlight.removeMesh(choice.disc)
            this.highlight.addMesh(choice.disc, color)
        },
        // Select a hotspot - propagate up to the parent.
        selectHotspot(choice) {
            this.$emit("select-choice", choice)
        },
        // Rotate the camera to face a hotspot, based on its position.
        focusHotspot(choice) {
            let x = choice.disc.position.x
            let z = choice.disc.position.z
            let y = choice.disc.position.y

            let hypot = Math.sqrt(Math.pow(x, 2) + Math.pow(z, 2))

            let alpha = 0
            if (x >= 0) {
                alpha = Math.atan(z / x) + Math.PI
            } else {
                alpha = Math.atan(z / x)
            }
            this.camera.alpha = alpha
            this.camera.beta = Math.atan(y / hypot) + Math.PI * 0.5
        },
        // Remove a hotspot from the scene.
        removeHotspot(choice) {
            choice.disc.dispose()
        },
        // Change a hotspot back to its original color and highlight.
        revertHotspotColor(choice) {
            if (choice.marker_color == 'transparent') {
                this.highlightActive.removeMesh(this.selectedChoice.disc)
            } else {
                this.highlightActive.removeMesh(this.selectedChoice.disc)
                let markerColorHex = this.getMarkerColorHex(choice)
                let rgbColor = hexToRgb(markerColorHex)
                let color = new Color3(rgbColor[0], rgbColor[1], rgbColor[2])
                this.highlight.addMesh(this.selectedChoice.disc, color)
                choice.disc.material.emissiveColor = color
            }
        },
        // Cancel the loading state on the selected hotspot.
        removeHotspotLoadingState() {
            this.revertHotspotColor(this.selectedChoice)
        },
        // Get the current angle of the camera.
        getCurrentCameraAngle() {
            return {
                alpha: this.camera.alpha,
                beta: this.camera.beta
            }
        },
        // Returns true if the choice has been selected, and the panel has been
        // configured to show selected choices.
        choiceIsRevealed(choice) {
            return choice.isDiscovered && this.panel.indicate_selected_choices
        },
        // Tab to focus on a hotspots, if tabbing is enabled.
        tabFocusChoice(event) {
            if (!this.enableTabbing) {
                return
            }
            event.preventDefault()
            if (this.tabbedChoiceIndex >= this.choices.length - 1) {
                this.tabbedChoiceIndex = 0
            } else {
                this.tabbedChoiceIndex ++ 
            }
            this.focusHotspot(this.choices[this.tabbedChoiceIndex])
        },
        // Select the currently tabbed choice. Allows for tabbing and then pressing
        // enter to select the choice.
        selectTabbedChoice() {
            if (!this.enableTabbing) {
                return
            }
            this.selectHotspot(this.choices[this.tabbedChoiceIndex])
        },
        // Destroy the Babylon scene and dispose of the engine.
        destroyScene() {
            if (this.scene) {
                // Destroy the scene before leaving.
                this.scene.dispose()
                this.engine.dispose()
            }
        },
        // Re-render the Babylon scene.
        resetScene() {
            this.destroyScene()
            this.init360Space()
        },
        // Returns the marker color for a choice, in hex format.
        getMarkerColorHex(choice) {
            let markerColor = choice.marker_color
            if (choice.isDiscovered && choice.change_color_when_explored && choice.marker_color_after_explored) {
                markerColor = choice.marker_color_after_explored
            }
            return hotspotColors[markerColor]
        },
    },
    mounted() {
        this.init360Space()
    },
    beforeDestroy() {
        this.destroyScene()
    },
}

// Convert a hex color code to RGB in decimal form.
function hexToRgb(hex) {
    let red = 255
    let green = 255
    let blue = 255
    if (hex !== "transparent") {
        let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        red = parseInt(result[1], 16)
        green = parseInt(result[2], 16)
        blue = parseInt(result[3], 16)
    }
    return [red / 255, green / 255, blue / 255]
}
</script>

<style lang="scss" scoped>
.editor-container {
    height: 720px;
    @media (min-height: 1200px) {
        height: 900px;
    }
    min-height: 400px;
    max-height: 65vh;
}

.render-canvas:focus-visible {
    outline: none;
}

.loading-spinner {
    background-color: rgb(255, 255, 255, 0.6);
}

.hotspots-viewport {
    min-height: 100%;
}

.vr-video {
    display: none;
    width: 1px;
    height: 1px;
}
</style>